import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Chart, ChartConfiguration } from 'chart.js';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { TranslationService } from 'src/app/services/translation.service';
import { IPersonalizedPracticeSessionReport } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-session-report';

@Component({
  selector: 'app-personalized-practice-sessions-chart',
  imports: [CommonModule, TranslocoModule],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'personalizedPractice',
    },
  ],
  templateUrl: './personalized-practice-sessions-chart.component.html',
  styleUrl: './personalized-practice-sessions-chart.component.scss',
})
export class PersonalizedPracticeSessionsChartComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() data: IPersonalizedPracticeSessionReport[] = [];
  @ViewChild('chartCanvas', { static: false })
  chartCanvas!: ElementRef<HTMLCanvasElement>;
  private chart: Chart | null = null;

  constructor(private translationService: TranslationService) {}

  ngAfterViewInit(): void {
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.chart) {
      this.updateChart();
    }
  }

  private initializeChart(): void {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');

    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }

    const chartData = this.prepareChartData();
    const xAxisConfig = this.calculateXAxisRange(chartData.length);

    const config: ChartConfiguration<'scatter'> = {
      type: 'scatter',
      data: {
        datasets: [
          {
            data: chartData,
            backgroundColor: '#52A4B0',
            borderColor: '#52A4B0',
            borderWidth: 1,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            min: xAxisConfig.min,
            max: xAxisConfig.max,
            title: {
              display: true,
              text: this.translationService.translate(
                'TIME',
                'personalizedPractice'
              ),
            },
            ticks: {
              display: false,
              stepSize: 0,
            },
            grid: {
              display: false,
            },
          },
          y: {
            min: 0,
            max: 5,
            ticks: {
              stepSize: 1,
            },
            title: {
              display: true,
              text: this.translationService.translate(
                'SCORE',
                'personalizedPractice'
              ),
            },
          },
        },
      },
    };

    this.chart = new Chart(ctx, config);
  }

  private prepareChartData(): {
    x: number;
    y: number;
    sessionDate: string;
    overallScore: number;
  }[] {
    if (!this.data || this.data.length === 0) {
      return [];
    }

    // Sort sessions by date to show chronological progression
    const sortedSessions = [...this.data].sort((a, b) => {
      return new Date(a.scoredAt).getTime() - new Date(b.scoredAt).getTime();
    });

    // Map each session to a point on the chart, filtering out sessions with null scores
    return sortedSessions
      .filter((session) => session.scoreOverall !== null)
      .map((session, index) => ({
        x: index,
        y: session.scoreOverall!,
        sessionDate: session.scoredAt,
        overallScore: session.scoreOverall!,
      }));
  }

  private calculateXAxisRange(dataLength: number): {
    min: number;
    max: number;
  } {
    // For 2 or fewer points, use a fixed range to keep dots closer together
    if (dataLength <= 2) {
      return { min: -1.5, max: 2.5 };
    }

    // For 3-4 points, add some padding
    if (dataLength <= 4) {
      return { min: -0.5, max: dataLength - 0.5 };
    }

    // For 5+ points, use minimal padding
    return { min: -0.2, max: dataLength - 0.8 };
  }

  private updateChart(): void {
    if (!this.chart) return;

    const chartData = this.prepareChartData();
    const xAxisConfig = this.calculateXAxisRange(chartData.length);

    this.chart.data.datasets[0].data = chartData;
    this.chart.options.scales!['x']!.min = xAxisConfig.min;
    this.chart.options.scales!['x']!.max = xAxisConfig.max;
    this.chart.update();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
